import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useAdminPermissions } from "@/hooks/useAdminPermissions";

export interface AdminNotification {
  id: string;
  title: string;
  message: string;
  type: string;
  read: boolean;
  link: string | null;
  created_at: string;
  department: string | null;
}

/**
 * Staff notification feed. Visibility is enforced by row-level access on
 * admin_notifications (department-scoped); read state is per staff member
 * via admin_notification_reads, so one person's "read" never clears another's.
 */
export function useAdminNotifications() {
  const { user } = useAuth();
  const { isStaff, loading: permissionsLoading } = useAdminPermissions();
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadByDepartment, setUnreadByDepartment] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  const apply = (rows: AdminNotification[]) => {
    setNotifications(rows);
    const unread = rows.filter((n) => !n.read);
    setUnreadCount(unread.length);
    const byDept: Record<string, number> = {};
    unread.forEach((n) => {
      const d = n.department || "general";
      byDept[d] = (byDept[d] || 0) + 1;
    });
    setUnreadByDepartment(byDept);
  };

  const fetchNotifications = useCallback(async () => {
    if (!user || permissionsLoading || !isStaff) {
      if (!permissionsLoading) setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("admin_notifications")
      .select("id,title,message,type,link,created_at,department")
      .order("created_at", { ascending: false })
      .limit(100);
    const rows = (data ?? []) as any[];
    let readIds = new Set<string>();
    if (rows.length) {
      const { data: reads } = await supabase
        .from("admin_notification_reads")
        .select("notification_id")
        .eq("user_id", user.id)
        .in("notification_id", rows.map((r) => r.id));
      readIds = new Set((reads ?? []).map((r: any) => r.notification_id));
    }
    apply(rows.map((r) => ({ ...r, read: readIds.has(r.id) })));
    setLoading(false);
  }, [user, isStaff, permissionsLoading]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!user || permissionsLoading || !isStaff) return;
    const channel = supabase
      .channel(`admin-notifications-data-${user.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "admin_notifications" }, () => fetchNotifications())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, isStaff, permissionsLoading, fetchNotifications]);

  const markAsRead = async (id: string) => {
    if (!user) return;
    apply(notifications.map((n) => (n.id === id ? { ...n, read: true } : n)));
    await supabase
      .from("admin_notification_reads")
      .upsert({ notification_id: id, user_id: user.id } as any, { onConflict: "notification_id,user_id", ignoreDuplicates: true });
  };

  const markAllAsRead = async () => {
    if (!user) return;
    const unread = notifications.filter((n) => !n.read);
    apply(notifications.map((n) => ({ ...n, read: true })));
    if (unread.length) {
      await supabase
        .from("admin_notification_reads")
        .upsert(unread.map((n) => ({ notification_id: n.id, user_id: user.id })) as any, {
          onConflict: "notification_id,user_id",
          ignoreDuplicates: true,
        });
    }
  };

  return {
    notifications,
    unreadCount,
    unreadByDepartment,
    loading,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications,
  };
}
